import {
  ComponentCollection,
  type Question,
  type QuestionCompositeModel,
  type QuestionDropdownModel,
  Serializer,
  type SurveyModel,
} from "survey-core";
import {
  type QuestionAddedEvent,
  type SurveyCreatorModel,
} from "survey-creator-core";
import { formatPhoneOrFaxNumberString } from "shared/components/survey-js-form/questions/profile-address-contact-information/formatPhoneOrFaxNumberString";
import {
  type ContactInformationFormData,
  type IProfileAddressContactInformationQuestion,
  type ProfileAddressContactInformationQuestionModel,
  type ProfileAddressFormData,
} from "shared/components/survey-js-form/questions/profile-address-contact-information/IProfileAddressContactInformationQuestion";
import {
  profileAddressContactInformationInternalElementNames,
  profileAddressContectInformationQuestionElements,
} from "shared/components/survey-js-form/questions/profile-address-contact-information/profileAddressContectInformationQuestionElements";
import { generateIteratedQuestionName } from "shared/components/survey-js-form/utils/geIteratedQuestionName";
import { type SessionView } from "shared/mappers/database/session/session";

export const custom_question_profile_address_contact_information =
  "profileaddresscontactinformation";

/**
 * Content elements that get no `_required`/`_visible` property registered and
 * no property applied to them. `ProfileAddressContactInformationQuestionProperties`
 * is derived from the same list, so what is registered here and what is
 * declared there stay in step.
 */
const isInternalElement = (name: string) =>
  (
    profileAddressContactInformationInternalElementNames as readonly string[]
  ).includes(name);

export type ProfileAddressContactInformationQuestionProps = {
  creator?: SurveyCreatorModel;
  session?: SessionView;
};

//TODO: More cleanup needed.
export const registerProfileAddressContactInformationQuestion = async ({
  creator,
  session,
}: ProfileAddressContactInformationQuestionProps) => {
  creator?.onQuestionAdded.add(
    (sender: SurveyCreatorModel, options: QuestionAddedEvent) => {
      if (options.question) {
        if (
          options.question?.getType() ==
          custom_question_profile_address_contact_information
        ) {
          options.question.name = generateIteratedQuestionName(
            custom_question_profile_address_contact_information,
            sender,
          );
        }
      }
    },
  );

  const onSurveyVisibleChanged = (sender: SurveyModel, options: any) => {
    if (
      options.question.getType() ===
      custom_question_profile_address_contact_information
    ) {
      contact_info_object.setValuesFromProperties(options.question);
    }
  };

  const formatPropertyName = (str: string) => {
    str = str.replace(/([A-Z0-9])/g, " $1");
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const singleCountryCode = [
    "United States",
    "Canada",
    "Bahamas, The",
    "Bermuda",
    "Cayman Islands",
  ];

  const contact_info_object: IProfileAddressContactInformationQuestion = {
    name: custom_question_profile_address_contact_information,
    title: "Address",
    elementsJSON: profileAddressContectInformationQuestionElements,
    onItemValuePropertyChanged: () => {
      //null
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onCreated(question: QuestionCompositeModel): void {
      //null
    },
    onInit() {
      Serializer.addProperty(
        custom_question_profile_address_contact_information,
        {
          name: "country_default",
          type: "singlevalue",
          displayName: formatPropertyName("Country"),
          category: "Default",
        },
      );
      Serializer.addProperty(
        custom_question_profile_address_contact_information,
        {
          name: "type_id",
          type: "dropdown",
          displayName: formatPropertyName("Type"),
          category: "Type & Use",
          choices:
            session?.society?.refProfileAddressAndContactInformationTypes?.map(
              (type) => ({
                text: type.description,
                value: type.refProfileAddressAndContactInformationTypeId,
              }),
            ) ?? [],
        },
      );
      Serializer.addProperty(
        custom_question_profile_address_contact_information,
        {
          name: "use_id",
          type: "multiplevalues",
          visible: false,
        },
      );
      Serializer.addProperty(
        custom_question_profile_address_contact_information,
        {
          name: "use_ids",
          type: "multiplevalues",
          displayName: formatPropertyName("Use"),
          category: "Type & Use",
          choices:
            session?.society?.refProfileAddressAndContactInformationUses?.map(
              (use) => ({
                text: use.description,
                value: use.refProfileAddressAndContactInformationUseId,
              }),
            ) ?? [],
        },
      );
      Serializer.addProperty(
        custom_question_profile_address_contact_information,
        {
          name: "region_default",
          type: "singlevalue",
          displayName: formatPropertyName("Region"),
          category: "Default",
        },
      );
      contact_info_object.elementsJSON.forEach((element: Question) => {
        if (isInternalElement(element.name)) {
          return;
        }
        Serializer.addProperty(
          custom_question_profile_address_contact_information,
          {
            name: `${element.name}_required`,
            type: "boolean",
            displayName: formatPropertyName(element.name + " Required"),
            default: false,
            category: "Required Fields",
          },
        );
        Serializer.addProperty(
          custom_question_profile_address_contact_information,
          {
            name: `${element.name}_visible`,
            type: "boolean",
            displayName: formatPropertyName(element.name + "  Visible"),
            default: true,
            category: "Visible Fields",
          },
        );
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    onAfterRender: (
      question: QuestionCompositeModel,
      htmlElement: any,
    ): void => {
      const countryQuestionValue =
        question.contentPanel.getQuestionByName("country")?.value;

      if (!countryQuestionValue) {
        return;
      }
      const stateQuestion = question.contentPanel.getQuestionByName("state");
      const usStateQuestion =
        question.contentPanel.getQuestionByName("us_state");

      usStateQuestion &&
        (usStateQuestion.visible =
          countryQuestionValue === "United States" &&
          question.getPropertyValue("us_state_visible"));

      stateQuestion &&
        (stateQuestion.visible =
          countryQuestionValue !== "United States" &&
          question.getPropertyValue("state_visible"));
    },
    onLoaded(question: ProfileAddressContactInformationQuestionModel) {
      if (!question.getSurvey()) {
        return;
      }

      question.clearIfInvisible = "none";
      this.setValuesFromProperties(question);
      const survey = question.getSurvey() as SurveyModel;
      if (survey.isDesignMode) {
        this.hideProperties();
      }
      if (!survey.onVisibleChanged.hasFunc(onSurveyVisibleChanged)) {
        survey.onVisibleChanged.add(onSurveyVisibleChanged);
      }
    },
    setValuesFromProperties(
      question: ProfileAddressContactInformationQuestionModel,
    ) {
      this.elementsJSON.forEach((element: Question) => {
        if (isInternalElement(element.name)) {
          return;
        }
        this.setVisibilityValue(question, element.name + "_visible");
        this.setDefaultValue(question, element.name + "_default");
        this.setRequiredValue(question, element.name + "_required");
      });

      const useIds =
        question.getPropertyValue("use_ids") ??
        question.getPropertyValue("use_id");

      question.setValue("use_ids", useIds, false, false);
      // deprecated content element kept in sync for backward compatibility
      question.setValue("use_id", useIds, false, false);

      const typeId =
        question.getPropertyValue("type_id") ??
        session?.society?.refProfileAddressAndContactInformationTypes?.[0]
          ?.refProfileAddressAndContactInformationTypeId;
      question.setValue("type", typeId, false, false);
      question.setValue("type_id", typeId, false, false);
    },
    hideProperties() {
      [
        "name",
        "visible",
        "isRequired",
        "readOnly",
        "enableIf",
        "requiredIf",
        "clearIfInvisible",
        "defaultValueExpression",
        "defaultValue",
        "startWithNewLine",
        "valueName",
        "useDisplayValuesInTitle",
        "correctAnswer",
        "requiredErrorText",
        "validators",
      ].forEach((prop) => {
        Serializer.getProperty(
          custom_question_profile_address_contact_information,
          prop,
        ).visible = false;
      });
    },
    onAfterRenderContentElement(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      question: QuestionCompositeModel,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      element: Question,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
      htmlElement: any,
    ): void {
      //null
    },
    setVisibilityValue(
      question: ProfileAddressContactInformationQuestionModel,
      propertyName: string,
    ) {
      const propertyValue = question.getPropertyValue(propertyName);
      const targetQuestion = question.contentPanel.getQuestionByName(
        propertyName.replace("_visible", ""),
      );
      if (targetQuestion.name === "us_state") {
        targetQuestion.visible =
          propertyValue &&
          question.contentPanel.getQuestionByName("country").value ===
            "United States";
        return;
      }
      if (targetQuestion.name === "state") {
        targetQuestion.visible =
          propertyValue &&
          question.contentPanel.getQuestionByName("country").value !==
            "United States";
        return;
      }
      if (targetQuestion) {
        targetQuestion.setPropertyValue("visible", propertyValue);
      }
      //maintenance note, visibility for email_option is set in the element json
    },
    setRequiredValue(
      question: ProfileAddressContactInformationQuestionModel,
      propertyName: string,
    ) {
      const questionName = propertyName.replace("_required", "");
      const targetQuestion =
        question.contentPanel.getQuestionByName(questionName);
      targetQuestion.setPropertyValue(
        "isRequired",
        question.isVisible && question.getPropertyValue(propertyName),
      );
    },
    disableDefaultValuesFromSimilarQuestions() {
      //TODO: disable default values from other questions
    },
    setDefaultValue(
      question: ProfileAddressContactInformationQuestionModel,
      propertyName: string,
    ) {
      if (!propertyName.endsWith("_default")) {
        return;
      }
      if (
        propertyName === "country_default" ||
        propertyName === "region_default"
      ) {
        const target_question = question.contentPanel.getQuestionByName(
          propertyName.replace("_default", ""),
        ) as QuestionDropdownModel;

        const value = Serializer.getObjPropertyValue(question, propertyName);

        target_question.defaultValue = value;
        return;
      }
    },
    onPropertyChanged(
      question: ProfileAddressContactInformationQuestionModel,
      propertyName: string,
    ) {
      if (
        propertyName.endsWith("_visible") ||
        propertyName.endsWith("_invisible")
      ) {
        this.setVisibilityValue(question, propertyName);
      } else if (propertyName.endsWith("_required")) {
        this.setRequiredValue(question, propertyName);
      } else if (propertyName.endsWith("_default")) {
        this.setDefaultValue(question, propertyName);
      }
    },
    getSimilarQuestions(
      question: QuestionCompositeModel,
    ): Array<QuestionCompositeModel> {
      const surveyModel = question.getSurvey() as unknown as SurveyModel;
      if (surveyModel.isDesignMode) {
        return [question];
      }
      const similarQuestion = surveyModel
        .getAllQuestions()
        .filter((similarQuestion: Question) => {
          if (
            similarQuestion.getType().toLocaleLowerCase() !==
            custom_question_profile_address_contact_information.toLocaleLowerCase()
          ) {
            return false;
          }
          return similarQuestion.isVisible;
        }) as QuestionCompositeModel[];
      return similarQuestion;
    },
    handlePhoneAndFaxFormatting(
      question: QuestionCompositeModel,
      name: string,
      newValue: string,
    ): void {
      const country = question.contentPanel.getQuestionByName("country").value;
      const isSingleCountryCode = singleCountryCode.includes(country);
      const displayFields = ["phone_display", "fax_display"];
      const fieldsToUpdate = ["phone", "fax"];

      if (displayFields.includes(name) && newValue.length) {
        const formattedValue = formatPhoneOrFaxNumberString(
          newValue,
          isSingleCountryCode,
        );
        question.contentPanel.getQuestionByName(name).value = formattedValue;
      }

      fieldsToUpdate.forEach((field) => {
        const displayFieldName = `${field}_display`;
        const displayValue =
          question.contentPanel.getQuestionByName(displayFieldName).value;
        let valueToUpdate = displayValue?.slice(0, 16);

        if (isSingleCountryCode) {
          valueToUpdate = valueToUpdate?.replace(/\D/g, "");
        }
        question.contentPanel.getQuestionByName(field).value = valueToUpdate;
      });
    },
    onValueChanged: function (
      question: QuestionCompositeModel,
      name: string,
      newValue: string | undefined,
    ): void {
      if (name === "phone_display" || name === "fax_display") {
        this.handlePhoneAndFaxFormatting(question, name, newValue!);
      }
      const isSingleCountryCode = singleCountryCode.includes(
        question.contentPanel.getQuestionByName("country").value,
      );
      const phoneFaxPlaceholder = isSingleCountryCode
        ? "X (XXX) XXX-XXXX"
        : "XXXXXXXXXX";
      question.contentPanel.getQuestionByName("phone_display").placeholder =
        phoneFaxPlaceholder;
      question.contentPanel.getQuestionByName("fax_display").placeholder =
        phoneFaxPlaceholder;

      if (name === "profileAddress" || name === "contactInformation") {
        return;
      }

      //DEV PsudoCode & Note
      // if (name === "postal_code") {
      //   Please look into the useGeocodeExpression for a hook on the "postal_code" question.
      // }

      const similarQuestions = this.getSimilarQuestions(question);
      similarQuestions.forEach((singleQuestion: QuestionCompositeModel) => {
        this.onValueChangedContactInformation(singleQuestion);
        this.onValueChangedProfileAddress(singleQuestion);
      });
    },
    onValueChangedContactInformation: (
      singleQuestion: QuestionCompositeModel,
    ): void => {
      const surveyModel = singleQuestion.getSurvey() as unknown as SurveyModel;
      const surveyData = surveyModel.getSurveyData();
      let contactInformationData: ContactInformationFormData[] =
        surveyData.getValue("contactInformation");
      contactInformationData = contactInformationData ?? [];
      let singleQuestionContactInformationData:
        | ContactInformationFormData
        | { question_name: string };
      const questionValues = singleQuestion.contentPanel.getValue();
      const contactInformationId = questionValues.contact_information_id;
      if (
        !contactInformationData.find(
          (data) =>
            data.question_name === singleQuestion.name ||
            (contactInformationId &&
              (data.contact_information_id ?? data.id) ===
                contactInformationId),
        )
      ) {
        singleQuestionContactInformationData = {
          question_name: singleQuestion.name,
        };
        contactInformationData.push(singleQuestionContactInformationData);
      }
      singleQuestionContactInformationData = contactInformationData.find(
        (data) =>
          data.question_name === singleQuestion.name ||
          (contactInformationId &&
            (data.contact_information_id ?? data.id) === contactInformationId),
      ) ?? {
        question_name: "ERROR",
      };
      singleQuestionContactInformationData.question_name = singleQuestion.name;
      singleQuestionContactInformationData.profile_form_question_name =
        singleQuestion.name;
      singleQuestionContactInformationData.email = questionValues.email;
      singleQuestionContactInformationData.phone = questionValues.phone;
      singleQuestionContactInformationData.fax = questionValues.fax;
      singleQuestionContactInformationData.phone = questionValues.phone;
      singleQuestionContactInformationData.use =
        questionValues.use_ids ?? questionValues.use_id;
      singleQuestionContactInformationData.use_ids =
        questionValues.use_ids ?? questionValues.use_id;
      singleQuestionContactInformationData.type_id =
        questionValues.type_id ?? questionValues.type;
      singleQuestionContactInformationData.type =
        questionValues.type_id ?? questionValues.type;
      if (questionValues.contact_information_id) {
        singleQuestionContactInformationData.contact_information_id =
          questionValues.contact_information_id;
        singleQuestionContactInformationData.id =
          questionValues.contact_information_id;
      }
      contactInformationData = contactInformationData.filter(
        (data) =>
          data === singleQuestionContactInformationData ||
          (data.question_name !== singleQuestion.name &&
            (!contactInformationId ||
              String(data.contact_information_id ?? data.id) !==
                String(contactInformationId))),
      );
      surveyData.setValue(
        "contactInformation",
        filterNonEmptyAddressContactInfo(contactInformationData),
        false,
        false,
      );
    },
    onValueChangedProfileAddress: (
      singleQuestion: QuestionCompositeModel,
    ): void => {
      const surveyModel = singleQuestion.getSurvey() as unknown as SurveyModel;

      const surveyData = surveyModel.getSurveyData();
      let profileAddressData: ProfileAddressFormData[] =
        surveyData.getValue("profileAddress");
      profileAddressData = profileAddressData ?? [];
      let singleQuestionProfileAddressData: ProfileAddressFormData;
      const questionValues = singleQuestion.contentPanel.getValue();
      const addressType = questionValues.type_id ?? questionValues.type;
      const existingProfileAddress = profileAddressData.find(
        (data) =>
          data.question_name === singleQuestion.name ||
          (!data.question_name && (data.type_id ?? data.type) === addressType),
      );
      if (!existingProfileAddress) {
        singleQuestionProfileAddressData = {
          question_name: singleQuestion.name,
        };
        profileAddressData.push(singleQuestionProfileAddressData);
      } else {
        singleQuestionProfileAddressData = existingProfileAddress;
      }
      singleQuestionProfileAddressData.question_name = singleQuestion.name;
      if (questionValues.country === "United States") {
        const us_state_question =
          singleQuestion.contentPanel.getQuestionByName("us_state");
        us_state_question.visible =
          singleQuestion.getPropertyValue("us_state_visible");
        const state_question =
          singleQuestion.contentPanel.getQuestionByName("state");
        state_question.visible = false;
      } else {
        const us_state_question =
          singleQuestion.contentPanel.getQuestionByName("us_state");
        us_state_question.visible = false;
        const state_question =
          singleQuestion.contentPanel.getQuestionByName("state");
        state_question.visible =
          singleQuestion.getPropertyValue("state_visible");
      }
      singleQuestionProfileAddressData.line1 = questionValues.line1;
      singleQuestionProfileAddressData.line2 = questionValues.line2;
      singleQuestionProfileAddressData.line3 = questionValues.line3;
      singleQuestionProfileAddressData.city = questionValues.city;
      singleQuestionProfileAddressData.state =
        singleQuestionProfileAddressData.country === "United States"
          ? questionValues.us_state
          : questionValues.state;
      singleQuestionProfileAddressData.country = questionValues.country;
      singleQuestionProfileAddressData.region = questionValues.region;
      singleQuestionProfileAddressData.postal_code = questionValues.postal_code;
      singleQuestionProfileAddressData.use =
        questionValues.use_ids ?? questionValues.use_id;
      singleQuestionProfileAddressData.use_ids =
        questionValues.use_ids ?? questionValues.use_id;
      singleQuestionProfileAddressData.type =
        questionValues.type_id ?? questionValues.type;
      singleQuestionProfileAddressData.type_id =
        questionValues.type_id ?? questionValues.type;

      surveyData.setValue(
        "profileAddress",
        filterNonEmptyAddressContactInfo(profileAddressData),
        false,
        false,
      );
    },
  };

  if (
    !ComponentCollection.Instance.getCustomQuestionByName(
      custom_question_profile_address_contact_information,
    )
  ) {
    ComponentCollection.Instance.add(contact_info_object);
  }

  const useIdsProperty = Serializer.getProperty(
    custom_question_profile_address_contact_information,
    "use_ids",
  );
  useIdsProperty.setChoices(
    session?.society?.refProfileAddressAndContactInformationUses?.map(
      (use) => ({
        text: use.description,
        value: use.refProfileAddressAndContactInformationUseId,
      }),
    ) ?? [],
  );
  useIdsProperty.visible = true;

  return contact_info_object;
};

export const filterNonEmptyAddressContactInfo = (
  data: ProfileAddressFormData[] | ContactInformationFormData[],
) =>
  data.filter(
    (item) =>
      item.type_id !== undefined &&
      [
        "line1",
        "line2",
        "line3",
        "city",
        "state",
        "region",
        "country",
        "postal_code",
        "email",
        "phone",
        "fax",
      ].some(
        (
          field:
            | keyof ProfileAddressFormData
            | keyof ContactInformationFormData,
        ) => {
          const value = item[field];

          return typeof value === "string"
            ? value.trim() !== ""
            : value !== null && value !== undefined;
        },
      ),
  );
